import React, {ReactNode, useMemo} from 'react';
import {Loader} from 'rsuite';
import {useSessionInfoQuery} from '@/api/useSessionInfoQuery';
import {useCartQuery} from '@/api/useCartQuery';
import {useCurrenciesStore} from '@/state/currenciesStore';
import { useAddresses } from '@/hooks/useAddresses';
import {formatCurrency, convertCurrencyAmount} from '@/utils/currencyUtils';
import VariantAddToCart from "@/components/variant/add-to-cart/VariantAddToCart";
import {cn} from '@bem-react/classname';
import styles from './VariantPrice.module.scss';

export type VariantPriceProps = {
  // Required identifiers
  variantId: number | string;
  productId?: number | string | null;

  // Variant pricing fields
  price: number; // variant.price (original/base price)
  salePrice?: number | null; // variant.salePrice
  onSale?: boolean; // variant.onSale
  ppd?: number | null; // variant.productPpd

  // Stock and status fields
  hasStock?: boolean; // variant.hasStock
  variantReleaseDate?: string | Date | null; // variant.productReleaseDate
  productReleaseDate?: string | Date | null; // variant.product.productReleaseDate

  // Tax/VAT
  vatCategory?: string | null; // e.g., Craft tax category handle: "general", "boker", etc.

  // UI/formatting props
  compact?: boolean; // mirrors twig variable
  formatTitle?: string | null; // variant.productFormat.one().title

  // Slot for add-to-cart button if the consumer wants to inject it
  addToCartSlot?: ReactNode;
  className?: string;
};

function isFuture(d?: string | Date | null): boolean {
  if (!d) return false;
  const dt = typeof d === 'string' ? new Date(d) : d;
  return !!dt && !isNaN(dt.getTime()) && dt.getTime() > Date.now();
}

function vatRateFromCategory(cat?: string | null): number {
  if (!cat) return 0;
  const c = String(cat).toLowerCase();
  if (c === 'general' || c === 'generalRow') return 0.25;
  if (c === 'boker' || c === 'books' || c === 'book') return 0.0;
  return 0; // default safe
}


export default function VariantPrice(props: VariantPriceProps) {
  const cName = cn(styles.cVariantPrice);
  const {
    variantId,
    price,
    salePrice = null,
    onSale = false,
    ppd = null,
    hasStock = true,
    variantReleaseDate = null,
    productReleaseDate = null,
    vatCategory = null,
    className = '',
  } = props;

  // Read session info from React Query cache (and network if not cached)
  const sessionQuery = useSessionInfoQuery();
  const session = sessionQuery.data;
  const usePpd = (session as any)?.usePpd || (session as any)?.usePPD || 0; // tolerate different casing

  // Selected currency from the cart
  const cartQuery = useCartQuery();
  const cart = cartQuery.data;
  const currency = cart?.paymentCurrency;

  // Currency conversion rates from store (injected via Twig into window and init on app start)
  const rates = useCurrenciesStore((s) => s.rates);

  // Determine shipping address id and fetch the full address for reliable country ISO
  const shippingAddressId: string | number | null = useMemo(() => {
    const c: any = cart as any;
    if (!c) return null;
    return c?.shippingAddressId ?? c?.selectedShippingAddressId ?? c?.shippingAddress?.id ?? null;
  }, [cart]);
  const { getAddress, appliesVat } = useAddresses(shippingAddressId);

  const {displayPrice, effectiveOnSale, originalPrice, ppdUsed} = useMemo(() => {
    // Base logic mirrors Twig:
    // - If user has PPD and ppd > 0 => use ppd, disable sale
    // - Else use salePrice if onSale else price
    let _onSale = !!onSale;
    let _display = _onSale && typeof salePrice === 'number' ? (salePrice as number) : price;
    let _original = price;
    let _ppdUsed = false;

    if (usePpd && typeof ppd === 'number' && ppd > 0) {
      _display = ppd;
      _onSale = false; // PPD overrides sale badge per Twig
      _ppdUsed = true;
    }

    return {displayPrice: _display, effectiveOnSale: _onSale, originalPrice: _original, ppdUsed: _ppdUsed};
  }, [onSale, price, salePrice, ppd, usePpd]);

  // Apply VAT only when shipping destination is Norway (NO)
  const applyVat = useMemo(() => {
    const preferred = getAddress((cart as any)?.shippingAddress);
    return appliesVat(preferred);
  }, [getAddress, appliesVat, (cart as any)?.shippingAddress]);

  const {displayWithVat, originalWithVat} = useMemo(() => {
    // If PPD is used, it should always be without VAT
    const rate = applyVat && !ppdUsed ? vatRateFromCategory(vatCategory) : 0;
    if (rate > 0) {
      const m = 1 + rate;
      return { displayWithVat: displayPrice * m, originalWithVat: originalPrice * m };
    }
    return { displayWithVat: displayPrice, originalWithVat: originalPrice };
  }, [displayPrice, originalPrice, vatCategory, applyVat, ppdUsed]);

  const {convDisplayPrice, convOriginalPrice} = useMemo(() => {
    const baseIso = 'NOK';
    return {
      convDisplayPrice: convertCurrencyAmount(displayWithVat, rates, baseIso, currency),
      convOriginalPrice: convertCurrencyAmount(originalWithVat, rates, baseIso, currency),
    };
  }, [currency, rates, displayWithVat, originalWithVat]);

  const statuses = useMemo(() => {
    const list: string[] = [];
    if (isFuture(variantReleaseDate) || isFuture(productReleaseDate)) {
      list.push('Preorder');
    }
    return list;
  }, [variantReleaseDate, productReleaseDate, effectiveOnSale]);

  const discountPct = useMemo(() => {
    if (!effectiveOnSale) return 0;
    if (!originalPrice || originalPrice <= 0) return 0;
    const raw = ((originalPrice - displayPrice) / originalPrice) * 100;
    const pct = Math.round(raw);
    return pct > 0 ? pct : 0;
  }, [effectiveOnSale, originalPrice, displayPrice]);

  if (!variantId) {
    return <div>No variant set</div>;
  }

  // Show a loading spinner until both cart and session have loaded
  if (sessionQuery.isLoading || cartQuery.isLoading) {
    return (
      <div className={cName({}, [className])}>
        <div className={cName('LoadingRow')}>
          <Loader size="sm"/>
        </div>
      </div>
    );
  }

  if (!hasStock) {
    return (
      <div className={cName({}, [className])}>
        <div className={cName('Inner')}>
          <div className={cName('SoldOut')}>Sold out</div>
        </div>
      </div>
    );
  }

  return (
    <div className={cName({}, [className])}>
        <div className={cName('StatusesRow')}>
          {!!statuses.length && (
            <div className={cName('Statuses')}>{statuses.join(', ')}</div>
          )}
        </div>

        <div className={cName('PriceRow')}>
          <div className={cName('Amounts')}>
            <div className={`${cName('Current')} ${effectiveOnSale ? cName('CurrentOnSale') : ''}`}>
              {formatCurrency(convDisplayPrice, currency)}
            </div>
            {effectiveOnSale && discountPct > 0 && (
              <div className={cName('DiscountBadge')}>
                -{discountPct}%
              </div>
            )}
          </div>
        </div>
        <div className={cName('AddToCart')}>
          <VariantAddToCart variantId={variantId}/>
        </div>
    </div>
  );
}
